import React from "react";

import { cx } from "@/lib/tailwind-utils";

interface FormSkeletonProps {
  className?: string;
}

export const FormSkeleton: React.FC<FormSkeletonProps> = ({
  className = "",
}) => {
  return (
    <div
      className={cx(
        "flex h-screen w-full flex-col overflow-hidden bg-background z-10",
        className,
      )}
    >
      <div className="flex h-full w-full animate-pulse flex-col">
        {/* Header */}
        <div className="flex w-full shrink-0 items-center justify-between gap-4 border-b border-border px-4 py-4 sm:border-none sm:px-9 sm:pb-0 sm:pt-4">
          {/* Logo skeleton */}
          <div className="h-12 w-40 rounded bg-muted"></div>

          {/* Navigation tabs skeleton */}
          <div className="relative hidden h-9 max-w-[70%] items-center justify-center gap-1 md:flex">
            <div className="h-6 w-6 rounded bg-muted"></div>
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="h-3.5 w-3.5 rounded-full border-2 border-border bg-background ring-4 ring-white"></div>
                  <div className="h-4 w-20 rounded bg-muted"></div>
                </div>
              ))}
            </div>
            <div className="h-6 w-6 rounded bg-muted"></div>
          </div>

          {/* Contact button skeleton */}
          <div className="h-9 w-28 rounded bg-accent-200"></div>
        </div>

        {/* Main content area */}
        <div className="flex min-h-0 w-full flex-1 overflow-y-auto">
          <div className="mx-3 mt-6 w-full md:mx-auto md:mt-8 md:w-auto">
            {/* Mobile step selector skeleton */}
            <div className="flex items-center gap-2 md:hidden">
              <div className="h-10 w-full rounded-md bg-muted"></div>
            </div>

            {/* Form card skeleton */}
            <div className="mt-6 flex justify-center md:mt-0">
              <div className="mx-auto mb-6 flex w-full flex-col gap-6 rounded-lg border border-border bg-background p-3.5 shadow-sm md:w-[480px]">
                {/* Form title */}
                <div className="h-8 w-40 rounded-md bg-muted"></div>

                {/* Form fields */}
                <div className="space-y-6">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="h-5 w-24 rounded-md bg-muted"></div>
                        <div className="h-3 w-3 rounded-full bg-muted"></div>
                      </div>
                      {i === 0 && (
                        <div className="h-3 w-64 rounded-md bg-muted"></div>
                      )}
                      {i === 1 ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="h-4 w-4 rounded-full border-2 border-border bg-background"></div>
                            <div className="h-4 w-16 rounded-md bg-muted"></div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="h-4 w-4 rounded-full border-2 border-border bg-background"></div>
                            <div className="h-4 w-20 rounded-md bg-muted"></div>
                          </div>
                        </div>
                      ) : (
                        <div className="h-10 w-full rounded-md border border-border bg-background-secondary"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Continue button and reserved save-status space */}
        <div className="w-full shrink-0 border-t border-border p-4">
          <div className="mx-auto grid w-full grid-cols-[minmax(0,480px)_8rem] items-center justify-center gap-3">
            <div className="h-10 w-full min-w-0 rounded-md bg-accent-200"></div>
            <div className="h-10 w-32"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormSkeleton;
